import {
  safeParseDate,
  calculateDurationHours,
  getIsoDayOfWeekForYmdInTimezone,
  parseAvailabilityBlockDateTime,
  getTimePartsInTimezone,
  getYmdInTimezone,
  formatLocalCalendarYmd
} from './dateUtils';
import { isApprovedBookingStatus, isPaidBookingPaymentStatus } from '../../../utils/bookingPayment';

/**
 * Utility functions for booking calculations
 */

/**
 * Get the first available start time (30-min slots) not in blockedTimes, at or after currentStartTime.
 * @param {Array<string>} blockedTimes - Blocked time strings (e.g. ['09:00', '09:30'])
 * @param {string} [currentStartTime] - Current start time (e.g. '09:00'); if provided, returns first available >= this
 * @returns {string|null} First available time or null if all blocked from currentStartTime onward
 */
export const getNextAvailableStartTime = (blockedTimes, currentStartTime) => {
  if (!blockedTimes || blockedTimes.length === 0) return currentStartTime || '00:00';
  const blocked = new Set(blockedTimes);
  const [startHour = 0, startMin = 0] = (currentStartTime || '00:00').split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  for (let hour = 0; hour < 24; hour++) {
    for (const min of [0, 30]) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      const slotMinutes = hour * 60 + min;
      if (slotMinutes >= startMinutes && !blocked.has(timeStr)) return timeStr;
    }
  }
  return null;
};

/**
 * First 30-min end after start that is >= minEnd and not in blockedEndTimes (for end picker).
 */
export const pickFirstValidEndAfterStart = (startTime, blockedEndTimes, minEndStr) => {
  if (!startTime || !minEndStr || !Array.isArray(blockedEndTimes)) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const startMins = sh * 60 + sm;
  const [meh, mem] = minEndStr.split(':').map(Number);
  const minEndMins = meh * 60 + mem;
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const mins = h * 60 + m;
      if (mins <= startMins) continue;
      if (mins < minEndMins) continue;
      const t = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      if (!blockedEndTimes.includes(t)) return t;
    }
  }
  return null;
};

/**
 * Filter blocks by booking mode using blocked_for from the availability API.
 * blocked_for: ["exclusive"] = block for full venue only; per-person still available.
 * blocked_for: ["exclusive", "per_person"] = block for both.
 * @param {Array} blocks - Array of block objects with blocked_for
 * @param {string} bookingType - 'full-venue'|'table' (per person)
 * @returns {Array} Filtered blocks
 */
export const filterBlocksByMode = (blocks, bookingType) => {
  if (!blocks || blocks.length === 0) return [];
  const modeKey = bookingType === 'full-venue' ? 'exclusive' : 'per_person';
  return blocks.filter((b) => {
    const bf = b.blocked_for || [];
    if (bf.length === 0) return true; // legacy: no blocked_for = block for both
    return bf.includes(modeKey);
  });
};

/** Close at 00:00 with a later open means "until midnight" (end of calendar day), not "closed all day". */
export const isEndOfDayCloseTime = (openMinutes, closeMinutes) =>
  closeMinutes === 0 && openMinutes > 0;

/** OH blocks are derived locally; API copies duplicate and mis-handle 00:00 close. */
export const filterAvailabilityBlocks = (blocks, bookingType) =>
  filterBlocksByMode(blocks, bookingType).filter((b) => b?.provider !== 'operating_hours');

/**
 * Same merge as VenueBookingSidebar blockedTimes: OH + API blocks (within open hours) + past slots.
 * @param {'full-venue'|'table'} bookingType
 */
export const buildMergedBlockedStartTimes = ({
  blocks,
  bookingType,
  date,
  venueTimezone,
  selectedDateYmd,
  operatingHoursBlocked,
  pastTimes,
  minHours,
  operatingHoursBlockedEnd
}) => {
  if (!date) return [];
  const filtered = filterAvailabilityBlocks(blocks, bookingType);
  const fromBlocks = getBlockedTimesForDate(date, filtered, venueTimezone, selectedDateYmd);
  const ohBlockedSet = new Set(operatingHoursBlocked || []);
  const slotsWithinHours = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = (i % 2) * 30;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }).filter((s) => !ohBlockedSet.has(s));
  const fromBlocksWithinHours = fromBlocks.filter((s) => slotsWithinHours.includes(s));
  const merged = [...new Set([...operatingHoursBlocked, ...fromBlocksWithinHours, ...(pastTimes || [])])].sort();
  if (minHours == null || Number(minHours) <= 0) return merged;
  const blocked = new Set(merged);
  for (const st of slotsWithinHours) {
    if (blocked.has(st)) continue;
    const minEndStr = minEndTimeFromStartAndMinHours(st, minHours);
    if (!minEndStr) {
      blocked.add(st);
      continue;
    }
    const blockedEnd = buildMergedBlockedEndTimesForStart({
      startTime: st,
      blocks,
      bookingType,
      date,
      venueTimezone,
      selectedDateYmd,
      operatingHoursBlockedEnd: operatingHoursBlockedEnd || operatingHoursBlocked,
      pastTimes
    });
    if (pickFirstValidEndAfterStart(st, blockedEnd, minEndStr) == null) blocked.add(st);
  }
  return Array.from(blocked).sort();
};

/** Minimum end time (HH:MM) for a start + venue min duration; caps at 23:59. */
export const minEndTimeFromStartAndMinHours = (startTime, minHours) => {
  if (!startTime || minHours == null || Number(minHours) <= 0) return null;
  const [hours, minutes] = startTime.split(':').map(Number);
  const startMinutes = hours * 60 + minutes;
  const minEndMinutes = startMinutes + Math.round(Number(minHours) * 60);
  if (minEndMinutes >= 24 * 60) return '23:59';
  const minEndH = Math.floor(minEndMinutes / 60);
  const minMins = minEndMinutes % 60;
  return `${String(minEndH).padStart(2, '0')}:${String(minMins).padStart(2, '0')}`;
};

/**
 * End-picker blocked slots: OH (closing slot) + past + overlap with calendar blocks for the chosen start.
 * Does NOT include raw busy *start* slots — ending exactly when a block begins (e.g. 09:00 end for 07:00–09:00) stays valid.
 */
export const buildMergedBlockedEndTimesForStart = ({
  startTime,
  blocks,
  bookingType,
  date,
  venueTimezone,
  selectedDateYmd,
  operatingHoursBlockedEnd,
  pastTimes
}) => {
  if (!date || !startTime) return [];
  const filtered = filterAvailabilityBlocks(blocks, bookingType);
  const ohBlockedSet = new Set(operatingHoursBlockedEnd || []);
  const slotsWithinHours = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = (i % 2) * 30;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }).filter((s) => !ohBlockedSet.has(s));
  const blocksWithinHours = filtered.filter((b) => {
    const slots = getBlockedTimesForDate(date, [b], venueTimezone, selectedDateYmd);
    return slots.some((s) => slotsWithinHours.includes(s));
  });
  const fromOverlap = getBlockedEndTimesForStartTime(
    startTime,
    blocksWithinHours,
    date,
    venueTimezone,
    selectedDateYmd
  );
  const mergedForEnd = [...new Set([...operatingHoursBlockedEnd, ...(pastTimes || [])])].sort();
  return [...new Set([...mergedForEnd, ...fromOverlap])].sort();
};

function hasValidBookingForMode({
  blocks,
  bookingType,
  date,
  venueTimezone,
  selectedDateYmd,
  operatingHoursBlocked,
  operatingHoursBlockedEnd,
  pastTimes,
  minHours
}) {
  const blockedStarts = buildMergedBlockedStartTimes({
    blocks,
    bookingType,
    date,
    venueTimezone,
    selectedDateYmd,
    operatingHoursBlocked,
    pastTimes
  });
  const blockedStartSet = new Set(blockedStarts);
  for (let hour = 0; hour < 24; hour++) {
    for (const min of [0, 30]) {
      const st = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      if (blockedStartSet.has(st)) continue;
      const minEndStr = minEndTimeFromStartAndMinHours(st, minHours);
      if (!minEndStr) continue;
      const blockedEnd = buildMergedBlockedEndTimesForStart({
        startTime: st,
        blocks,
        bookingType,
        date,
        venueTimezone,
        selectedDateYmd,
        operatingHoursBlockedEnd,
        pastTimes
      });
      if (pickFirstValidEndAfterStart(st, blockedEnd, minEndStr) != null) return true;
    }
  }
  return false;
}

/**
 * True when neither full-venue nor per-person mode has any valid 30-min start + min-duration end pair (calendar preview).
 * Uses the same overlap rules as the end picker (block interval half-open vs booking).
 */
export const isDateFullyUnavailableForBothModes = ({
  blocksExclusive,
  blocksPerPerson,
  date,
  venueTimezone,
  selectedDateYmd,
  operatingHoursBlockedExclusive,
  operatingHoursBlockedPerPerson,
  operatingHoursBlockedEndExclusive,
  operatingHoursBlockedEndPerPerson,
  pastTimes,
  minHoursExclusive = 2,
  minHoursPerPerson = 2,
  perPersonEnabled = true
}) => {
  const okEx = hasValidBookingForMode({
    blocks: blocksExclusive,
    bookingType: 'full-venue',
    date,
    venueTimezone,
    selectedDateYmd,
    operatingHoursBlocked: operatingHoursBlockedExclusive,
    operatingHoursBlockedEnd: operatingHoursBlockedEndExclusive,
    pastTimes,
    minHours: minHoursExclusive
  });
  if (!perPersonEnabled) return !okEx;
  const okPp = hasValidBookingForMode({
    blocks: blocksPerPerson,
    bookingType: 'table',
    date,
    venueTimezone,
    selectedDateYmd,
    operatingHoursBlocked: operatingHoursBlockedPerPerson,
    operatingHoursBlockedEnd: operatingHoursBlockedEndPerPerson,
    pastTimes,
    minHours: minHoursPerPerson
  });
  return !okEx && !okPp;
};

/**
 * Full venue: use exclusive (full-venue) operating hours only.
 * Per person: merge per weekday — if that day exists in per-person hours, use it; otherwise inherit that day from full-venue hours (matches OperatingHoursForm).
 * Without this, a partial per-person schedule made the app use only per-person keys and dropped full-venue days, or used the wrong open time.
 * @param {'full-venue'|'table'} bookingType - 'table' = per person
 */
export function mergeOperatingHoursForBookingMode(bookingType, operatingHours, operatingHoursPerPerson) {
  const full = operatingHours && typeof operatingHours === 'object' ? operatingHours : {};
  if (bookingType === 'full-venue') {
    return full;
  }
  const per = operatingHoursPerPerson && typeof operatingHoursPerPerson === 'object' ? operatingHoursPerPerson : {};
  const merged = {};
  for (let day = 1; day <= 7; day++) {
    if (per[day] != null) {
      merged[day] = per[day];
    } else if (full[day] != null) {
      merged[day] = full[day];
    }
  }
  return merged;
}

/** HH:MM string comparable lexicographically after zero-padding hour/minute. */
const normalizeHmForCompare = (t) => {
  if (t == null || t === '') return '00:00';
  const raw = String(t).trim();
  const parts = raw.split(':');
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Rule covers the full calendar day (no hourly band).
 * Matches calendar aggregation: no times, or both midnight short/long form.
 */
export const isFullDayDynamicRule = (rule) => {
  if (!rule) return false;
  const st = rule.start_time;
  const et = rule.end_time;
  if ((st == null || st === '') && (et == null || et === '')) return true;
  const a = String(st || '').trim();
  const b = String(et || '').trim();
  if (
    (a === '00:00:00' || a === '00:00') &&
    (b === '00:00:00' || b === '00:00')
  ) {
    return true;
  }
  return false;
};

/**
 * Whether the booking [start,end] lies entirely inside the rule's time band (supports overnight bands).
 * @param {string} stNorm - normalized HH:MM
 * @param {string} etNorm - normalized HH:MM
 */
export const bookingFitsDynamicTimeBand = (stNorm, etNorm, rule) => {
  const ruleStart = normalizeHmForCompare(rule?.start_time);
  const ruleEnd = normalizeHmForCompare(rule?.end_time);
  if (ruleStart > ruleEnd) {
    return (
      (stNorm >= ruleStart || stNorm <= ruleEnd) &&
      (etNorm >= ruleStart || etNorm <= ruleEnd)
    );
  }
  return stNorm >= ruleStart && etNorm <= ruleEnd;
};

/**
 * Full-venue hourly from rules already filtered to the booking date.
 * 1) Time-banded rules that contain [start,end]; 2) full-day rules for that date; 3) base venue rate.
 */
export const resolveFullVenueDynamicHourlyRate = (applicableRules, startTime, endTime, baseHourlyRate) => {
  const base = Number(baseHourlyRate) || 0;
  if (!applicableRules?.length) {
    return { rate: base, matchedDynamicRule: false };
  }
  const stNorm = normalizeHmForCompare(
    startTime?.length >= 5 ? startTime : `${startTime || '00:00'}:00`
  );
  const etNorm = normalizeHmForCompare(
    endTime?.length >= 5 ? endTime : `${endTime || '00:00'}:00`
  );

  const timeRestricted = applicableRules.filter(
    (r) => !isFullDayDynamicRule(r) && r.start_time && r.end_time
  );
  for (const rule of timeRestricted) {
    if (bookingFitsDynamicTimeBand(stNorm, etNorm, rule)) {
      const p = Number(rule.price_per_hour);
      return { rate: Number.isFinite(p) ? p : base, matchedDynamicRule: true };
    }
  }

  const fullDay = applicableRules.filter((r) => isFullDayDynamicRule(r));
  if (fullDay.length > 0) {
    const p = Number(fullDay[0].price_per_hour);
    return { rate: Number.isFinite(p) ? p : base, matchedDynamicRule: true };
  }

  return { rate: base, matchedDynamicRule: false };
};

/**
 * Get per-person rate from dynamic per-person rules or default per-person price.
 * Priority: 1) dynamic per-person rule for date/time, 2) default per-person price, 3) null.
 * @param {string|Date} date - Selected date
 * @param {string} [startTime] - Start time (HH:MM or HH:MM:SS)
 * @param {string} [endTime] - End time (HH:MM or HH:MM:SS)
 * @param {Array} [dynamicPricingPerPerson] - Rules from GET /venues/{id}/dynamic-pricing-per-person
 * @param {number} [defaultPerPersonPrice] - From GET /venues/{id}/default-per-person-price
 * @returns {number|null} Per-person rate or null if neither exists (use current/API logic)
 */
export const getActualPerPersonPrice = (date, startTime, endTime, dynamicPricingPerPerson, defaultPerPersonPrice) => {
  const dateStr =
    typeof date === 'string'
      ? String(date).split('T')[0]
      : date instanceof Date && !isNaN(date.getTime())
        ? formatLocalCalendarYmd(date)
        : '';
  if (!dateStr) return null;

  if (dynamicPricingPerPerson?.length) {
    const applicableRules = dynamicPricingPerPerson.filter((rule) => {
      if (!rule.start_date || !rule.end_date) return false;
      const [sY, sM, sD] = rule.start_date.split('-').map(Number);
      const [eY, eM, eD] = rule.end_date.split('-').map(Number);
      const [dY, dM, dD] = dateStr.split('-').map(Number);
      const startDate = new Date(sY, sM - 1, sD);
      const endDate = new Date(eY, eM - 1, eD);
      const selectedDateObj = new Date(dY, dM - 1, dD);
      return selectedDateObj >= startDate && selectedDateObj <= endDate;
    });
    if (applicableRules.length > 0) {
      const timeRestrictedRules = applicableRules.filter(
        (rule) => !isFullDayDynamicRule(rule) && rule.start_time && rule.end_time
      );
      const stNorm = normalizeHmForCompare(
        startTime?.length >= 5 ? startTime : `${startTime || '00:00'}:00`
      );
      const etNorm = normalizeHmForCompare(
        endTime?.length >= 5 ? endTime : `${endTime || '00:00'}:00`
      );
      if (timeRestrictedRules.length > 0) {
        for (const rule of timeRestrictedRules) {
          if (bookingFitsDynamicTimeBand(stNorm, etNorm, rule)) {
            return Number(rule.price_per_person);
          }
        }
        // Time bands exist for this date but the booking is outside all of them —
        // use the venue default below, not a stale full-day override.
      } else {
        const fullDayRules = applicableRules.filter((r) => isFullDayDynamicRule(r));
        if (fullDayRules.length > 0) {
          return Number(fullDayRules[0].price_per_person);
        }
      }
    }
  }

  if (defaultPerPersonPrice != null && defaultPerPersonPrice !== '' && Number(defaultPerPersonPrice) >= 0) {
    return Number(defaultPerPersonPrice);
  }

  return null;
};

/**
 * Resolve per-person rate from configured prices only.
 * 1) dynamic per-person rule, 2) venue default, 3) space/preview rate when it is not hourly×10%.
 * Venue default wins over a stale space.per_person_price — the settings UI only edits the venue default.
 * Never invent a rate from exclusive hourly × 0.1.
 */
export const resolvePerPersonRateForBooking = ({
  availabilityPreview = null,
  date,
  startTime,
  endTime,
  dynamicPricingPerPerson,
  defaultPerPersonPrice,
  defaultHourlyPrice = 0
} = {}) => {
  const dynamicRate = getActualPerPersonPrice(
    date,
    startTime,
    endTime,
    dynamicPricingPerPerson,
    null
  );
  if (dynamicRate != null) return dynamicRate;

  if (defaultPerPersonPrice != null && defaultPerPersonPrice !== '' && Number(defaultPerPersonPrice) >= 0) {
    return Number(defaultPerPersonPrice);
  }

  const previewRate = getPerPersonRateFromAvailabilityPreview(availabilityPreview);
  const tenPercentOfHourly = (Number(defaultHourlyPrice) || 0) * 0.1;
  const previewIsHourlyStamp =
    previewRate != null &&
    tenPercentOfHourly > 0 &&
    Math.abs(previewRate - tenPercentOfHourly) < 0.02;
  if (previewRate != null && !previewIsHourlyStamp) return previewRate;

  return null;
};

/**
 * Read per-person rate from availability preview payload (snake_case or camelCase from API).
 */
export const getPerPersonRateFromAvailabilityPreview = (preview) => {
  if (!preview || typeof preview !== 'object') return null;
  const raw =
    preview.per_person_rate ??
    preview.perPersonRate ??
    preview.pricing?.per_person_rate ??
    preview.pricing?.perPersonRate;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Convert busy times to blocked time strings for a specific date
 * @param {Date} date - The date to get blocked times for
 * @param {Array} busyTimes - Array of busy time objects with start_time/start and end_time/end
 * @param {string} [venueTimezone] - Venue IANA zone; naive API datetimes are interpreted as this wall time
 * @param {string|null} [selectedDateYmd] - Selected calendar YYYY-MM-DD (prefer UI string over deriving from `date`)
 * @returns {Array<string>} Array of time strings in HH:MM format (e.g., ['08:00', '10:30', '14:00'])
 */
export const getBlockedTimesForDate = (date, busyTimes, venueTimezone = 'Europe/London', selectedDateYmd = null) => {
  if (!date || !busyTimes || busyTimes.length === 0) {
    return [];
  }
  
  if (isNaN(date.getTime())) {
    return [];
  }
  
  const ymdOpt = selectedDateYmd && String(selectedDateYmd).split('T')[0];
  const dateStr =
    ymdOpt && /^\d{4}-\d{2}-\d{2}$/.test(ymdOpt) ? ymdOpt : getYmdInTimezone(date, venueTimezone);
  if (!dateStr) return [];
  const blockedTimes = new Set();

  
  busyTimes.forEach((busyTime, index) => {
    const startTime = busyTime.start_time || busyTime.start;
    const endTime = busyTime.end_time || busyTime.end;
    
    if (!startTime || !endTime) return;
    
    const startDate = parseAvailabilityBlockDateTime(startTime, venueTimezone, busyTime.provider);
    const endDate = parseAvailabilityBlockDateTime(endTime, venueTimezone, busyTime.provider);
    
    if (!startDate || !endDate) {
      return;
    }
    
    const busyStartDate = getYmdInTimezone(startDate, venueTimezone);
    const busyEndDate = getYmdInTimezone(endDate, venueTimezone);
    
    // Check if the busy time overlaps with the selected date
    if (dateStr >= busyStartDate && dateStr <= busyEndDate) {
      // Calculate the actual time range on the selected date
      let blockStartHour, blockStartMin, blockEndHour, blockEndMin;
      
      if (dateStr === busyStartDate) {
        const startParts = getTimePartsInTimezone(startDate, venueTimezone);
        blockStartHour = startParts.hour;
        blockStartMin = startParts.minute;
      } else {
        // Busy time started before this date - block from midnight
        blockStartHour = 0;
        blockStartMin = 0;
      }
      
      if (dateStr === busyEndDate) {
        const endParts = getTimePartsInTimezone(endDate, venueTimezone);
        blockEndHour = endParts.hour;
        blockEndMin = endParts.minute;
      } else {
        // Busy time ends after this date - block until end of day
        blockEndHour = 23;
        blockEndMin = 59; // Block until 23:59, but we'll round to 23:30 for 30-min intervals
      }
      
      // Round start time down to nearest 30-minute interval
      if (blockStartMin > 0 && blockStartMin < 30) {
        blockStartMin = 0;
      } else if (blockStartMin >= 30) {
        blockStartMin = 30;
      }
      
      // Round end time up to nearest 30-minute interval
      if (blockEndMin > 0 && blockEndMin <= 30) {
        blockEndMin = 30;
      } else if (blockEndMin > 30) {
        blockEndMin = 0;
        blockEndHour += 1;
        if (blockEndHour >= 24) {
          blockEndHour = 23;
          blockEndMin = 30;
        }
      }
      
      // Generate 30-min *start* slots inside [blockStart, blockEnd) — block end is exclusive, so a booking may end at blockStart (e.g. busy 09:00–10:00 allows 07:00–09:00).
      let currentHour = blockStartHour;
      let currentMin = blockStartMin;
      
      while (currentHour < blockEndHour || (currentHour === blockEndHour && currentMin < blockEndMin)) {
        const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
        blockedTimes.add(timeStr);
        
        // Move to next 30-minute interval
        currentMin += 30;
        if (currentMin >= 60) {
          currentMin = 0;
          currentHour += 1;
        }
        
        // Safety check to prevent infinite loop
        if (currentHour >= 24) break;
      }
    }
  });
  
  const result = Array.from(blockedTimes).sort();
  
  return result;
};

/**
 * Get end times that would cause [startTime, endTime) to overlap any busy block (half-open intervals).
 * Only blocks end slots where the booking interval actually intersects the block — not every slot after block start.
 * @param {string} startTime - Start time HH:MM
 * @param {Array} blocks - Block objects with start_time, end_time
 * @param {Date} date - Selected date
 * @param {string} [venueTimezone] - Venue IANA zone
 * @param {string|null} [selectedDateYmd] - Selected calendar YYYY-MM-DD
 * @returns {string[]} End time strings to block
 */
export const getBlockedEndTimesForStartTime = (startTime, blocks, date, venueTimezone = 'Europe/London', selectedDateYmd = null) => {
  if (!startTime || !blocks?.length || !date || isNaN(date.getTime())) return [];
  const ymdOpt = selectedDateYmd && String(selectedDateYmd).split('T')[0];
  const dateStr =
    ymdOpt && /^\d{4}-\d{2}-\d{2}$/.test(ymdOpt) ? ymdOpt : getYmdInTimezone(date, venueTimezone);
  if (!dateStr) return [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  const blocked = new Set();

  blocks.forEach((busyTime) => {
    const st = busyTime.start_time || busyTime.start;
    const et = busyTime.end_time || busyTime.end;
    if (!st || !et) return;
    const startDate = parseAvailabilityBlockDateTime(st, venueTimezone, busyTime.provider);
    const endDate = parseAvailabilityBlockDateTime(et, venueTimezone, busyTime.provider);
    if (!startDate || !endDate) return;
    const busyStartDate = getYmdInTimezone(startDate, venueTimezone);
    const busyEndDate = getYmdInTimezone(endDate, venueTimezone);
    if (dateStr < busyStartDate || dateStr > busyEndDate) return;

    let blockStartHour, blockStartMin, blockEndHour, blockEndMin;
    if (dateStr === busyStartDate) {
      const p = getTimePartsInTimezone(startDate, venueTimezone);
      blockStartHour = p.hour;
      blockStartMin = p.minute;
    } else {
      blockStartHour = 0;
      blockStartMin = 0;
    }
    if (dateStr === busyEndDate) {
      const p = getTimePartsInTimezone(endDate, venueTimezone);
      blockEndHour = p.hour;
      blockEndMin = p.minute;
    } else {
      blockEndHour = 23;
      blockEndMin = 30;
    }
    const blockStartMinutes = blockStartHour * 60 + blockStartMin;
    const blockEndMinutes = blockEndHour * 60 + blockEndMin;

    // Booking [start, end) overlaps [blockStart, blockEnd) iff start < blockEnd && end > blockStart
    if (startMinutes >= blockEndMinutes) return;

    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const endMinutes = h * 60 + m;
        if (endMinutes <= startMinutes) continue;
        if (startMinutes < blockEndMinutes && endMinutes > blockStartMinutes) {
          blocked.add(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        }
      }
    }
  });
  return Array.from(blocked).sort();
};

// Calendar civil date in the browser's local zone (matches Date from dashboard calendar clicks).
// Do not use UTC getters or toISOString().split('T')[0] — those shift the day by viewer offset.
const calendarDayToYmd = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const ymd = String(date).trim().split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : '';
  }
  if (isNaN(date.getTime())) return '';
  return formatLocalCalendarYmd(date);
};

/** Civil event date from a booking row — never shifted by viewer timezone. */
export const normalizeBookingDateYmd = (booking) => {
  if (!booking) return '';
  const raw = booking.booking_date ?? booking.date ?? booking.event_date;
  if (!raw) return '';
  if (typeof raw === 'string') {
    const ymd = String(raw).trim().split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  }
  if (raw instanceof Date && !isNaN(raw.getTime())) {
    return formatLocalCalendarYmd(raw);
  }
  return '';
};

/** @deprecated use calendarDayToYmd */
const getDateStr = calendarDayToYmd;

/**
 * Get blocked time slots (HH:MM) for a date based on operating hours and holidays.
 * - If date is a holiday: all 30-min slots for the day are blocked.
 * - If venue is closed that day (is_open=false): all slots blocked.
 * - If venue is open: slots before open_time and after close_time are blocked (close is exclusive for start slots).
 * @param {Date} date - The date to check
 * @param {Object} operatingHours - Keyed by day 1-7 (1=Mon, 7=Sun), each: { is_open, open_time, close_time }
 * @param {Array} holidays - Array of { holiday_date, date, ... } with YYYY-MM-DD
 * @param {{ inclusiveClosingSlot?: boolean, timezone?: string, dateYmd?: string }} [options] - inclusiveClosingSlot: end picker may use closing slot. timezone: venue IANA zone. dateYmd: selected YYYY-MM-DD so weekday/holidays match that calendar day in the venue (not UTC instant).
 * @returns {string[]} Array of HH:MM time strings to block
 */
export const getBlockedTimesFromOperatingHoursAndHolidays = (date, operatingHours, holidays, options = {}) => {
  const inclusiveClosingSlot = options.inclusiveClosingSlot === true;
  const timezone = options.timezone || 'Europe/London';
  const dateYmdOpt = options.dateYmd && String(options.dateYmd).split('T')[0];
  if (!date || isNaN(date.getTime())) return [];
  const dateStr =
    dateYmdOpt && /^\d{4}-\d{2}-\d{2}$/.test(dateYmdOpt) ? dateYmdOpt : getDateStr(date);

  // Check if date is a holiday
  const isHoliday = Array.isArray(holidays) && holidays.some((h) => {
    const hDate = h.holiday_date || h.date || h.holidayDate || '';
    return String(hDate).split('T')[0] === dateStr;
  });
  if (isHoliday) {
    const all = [];
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        all.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return all;
  }

  // Weekday for operating hours must match the *selected calendar date* in the venue timezone (1=Mon…7=Sun).
  // Using UTC weekday of Date('YYYY-MM-DDT00:00:00Z') can be wrong (e.g. Friday local vs Saturday UTC).
  const dayNumber = getIsoDayOfWeekForYmdInTimezone(dateStr, timezone);
  const dayHours = operatingHours?.[dayNumber];
  // If no operating hours config at all, don't block (assume 24/7)
  if (!operatingHours || Object.keys(operatingHours).length === 0) return [];
  if (!dayHours || !dayHours.is_open) {
    const all = [];
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        all.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return all;
  }

  const openStr = (dayHours.open_time || '00:00:00').substring(0, 5);
  const closeStr = (dayHours.close_time || '23:59:00').substring(0, 5);
  const [openH, openM] = openStr.split(':').map(Number);
  const [closeH, closeM] = closeStr.split(':').map(Number);
  const openMinutes = openH * 60 + (openM || 0);
  const closeMinutes = closeH * 60 + (closeM || 0);
  const closesAtMidnight = isEndOfDayCloseTime(openMinutes, closeMinutes);

  const blocked = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const slotMinutes = h * 60 + m;
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      const afterClose = closesAtMidnight
        ? false
        : (inclusiveClosingSlot ? slotMinutes > closeMinutes : slotMinutes >= closeMinutes);
      if (slotMinutes < openMinutes || afterClose) {
        blocked.push(timeStr);
      }
    }
  }
  return blocked;
};

export const getBusyTimesForDate = (date, busyTimesList, timezone = 'Europe/London') => {
  if (!date || !busyTimesList) {
    return [];
  }

  if (isNaN(date.getTime())) {
    return [];
  }

  const dateStr = getYmdInTimezone(date, timezone);

  const filtered = busyTimesList.filter((busyTime) => {
    const startTime = busyTime.start_time || busyTime.start;
    const endTime = busyTime.end_time || busyTime.end;

    if (!startTime || !endTime) {
      return false;
    }

    const startDate = parseAvailabilityBlockDateTime(startTime, timezone, busyTime.provider) || safeParseDate(startTime);
    const endDate = parseAvailabilityBlockDateTime(endTime, timezone, busyTime.provider) || safeParseDate(endTime);

    if (!startDate || !endDate) {
      return false;
    }

    const busyStartDate = getYmdInTimezone(startDate, timezone);

    // Only include busy times that START on the selected calendar day in the venue timezone
    return busyStartDate === dateStr;
  });

  return filtered;
};

/**
 * Get busy times that overlap with the given date (not just start on it).
 * Used for weekly/monthly views where we need to show blocks on all days they span.
 * Uses venue `timezone` for civil dates (not UTC) so BST and multi-day spans match day columns.
 */
export const getBusyTimesOverlappingDate = (date, busyTimesList, timezone = 'Europe/London') => {
  if (!date || !busyTimesList) return [];
  if (isNaN(date.getTime())) return [];
  const dateStr = getYmdInTimezone(date, timezone);

  return busyTimesList.filter((busyTime) => {
    const startTime = busyTime.start_time || busyTime.start;
    const endTime = busyTime.end_time || busyTime.end;
    if (!startTime || !endTime) return false;
    const startDate = parseAvailabilityBlockDateTime(startTime, timezone, busyTime.provider) || safeParseDate(startTime);
    const endDate = parseAvailabilityBlockDateTime(endTime, timezone, busyTime.provider) || safeParseDate(endTime);
    if (!startDate || !endDate) return false;
    const busyStartDate = getYmdInTimezone(startDate, timezone);
    const busyEndDate = getYmdInTimezone(endDate, timezone);
    return dateStr >= busyStartDate && dateStr <= busyEndDate;
  });
};

export const calculateBlockedHours = (busyTimes, bookings, selectedDate) => {
  if (!selectedDate || isNaN(selectedDate.getTime())) return { busy: 0, booked: 0 };
  
  const selectedDateStr = getDateStr(selectedDate);
  
  // Calculate blocked hours from busy times
  const busyBlockedHours = busyTimes.reduce((total, busyTime) => {
    const startTimeStr = busyTime.start_time || busyTime.start;
    const endTimeStr = busyTime.end_time || busyTime.end;
    const startTime = startTimeStr ? safeParseDate(startTimeStr) : null;
    const endTime = endTimeStr ? safeParseDate(endTimeStr) : null;
    
    if (!startTime || !endTime) return total;
    
    const durationMs = endTime - startTime;
    const durationHours = durationMs / (1000 * 60 * 60);
    return total + (isNaN(durationHours) ? 0 : durationHours);
  }, 0);
  
  // Calculate blocked hours from confirmed bookings
  const bookedBlockedHours = bookings
    .filter((b) => normalizeBookingStatusKey(b) === 'confirmed')
    .reduce((total, booking) => {
      if (normalizeBookingDateYmd(booking) !== selectedDateStr) return total;

      return total + calculateDurationHours(booking.start_time || booking.startTime, booking.end_time || booking.endTime);
    }, 0);
  
  return {
    busy: busyBlockedHours,
    booked: bookedBlockedHours,
    total: busyBlockedHours + bookedBlockedHours
  };
};

export const getBookingsForDate = (bookings, date, status = null) => {
  const dateStr = calendarDayToYmd(date);
  if (!dateStr) return [];

  return bookings.filter((booking) => {
    const bookingDateStr = normalizeBookingDateYmd(booking);
    const matchesDate = bookingDateStr === dateStr;
    const matchesStatus = status ? normalizeBookingStatusKey(booking) === status : true;
    return matchesDate && matchesStatus;
  });
};

export const isHourBooked = (hour, bookings, date) => {
  if (!date || isNaN(date.getTime())) return false;
  
  const dateStr = getDateStr(date);
  
  return bookings
    .filter((b) => normalizeBookingStatusKey(b) === 'confirmed')
    .some((booking) => {
      if (normalizeBookingDateYmd(booking) !== dateStr) return false;

      const startTime = booking.start_time || booking.startTime;
      const endTime = booking.end_time || booking.endTime;
      
      if (!startTime || !endTime) return false;
      
      const [startHour] = startTime.split(':').map(Number);
      const [endHour] = endTime.split(':').map(Number);
      
      return hour >= startHour && hour < endHour;
    });
};

export const isHourBusy = (hour, busyTimes, date, timezone = 'Europe/London') => {
  if (!date || isNaN(date.getTime()) || !busyTimes?.length) return false;
  const dateStr = calendarDayToYmd(date);
  if (!dateStr) return false;
  const slotMinutes = hour * 60;
  return busyTimes.some((busyTime) => {
    const startTimeStr = busyTime.start_time || busyTime.start;
    const endTimeStr = busyTime.end_time || busyTime.end;
    const startTime = parseAvailabilityBlockDateTime(startTimeStr, timezone, busyTime.provider) || safeParseDate(startTimeStr);
    const endTime = parseAvailabilityBlockDateTime(endTimeStr, timezone, busyTime.provider) || safeParseDate(endTimeStr);
    if (!startTime || !endTime) return false;
    const startYmd = getYmdInTimezone(startTime, timezone);
    const endYmd = getYmdInTimezone(endTime, timezone);
    if (dateStr < startYmd || dateStr > endYmd) return false;
    let startMinutes = 0;
    let endMinutes = 24 * 60;
    if (dateStr === startYmd) {
      const p = getTimePartsInTimezone(startTime, timezone);
      startMinutes = p.hour * 60 + p.minute;
    }
    if (dateStr === endYmd) {
      const p = getTimePartsInTimezone(endTime, timezone);
      endMinutes = p.hour * 60 + p.minute;
    }
    return slotMinutes >= startMinutes && slotMinutes < endMinutes;
  });
};

/**
 * Check if a date has any busy times
 * @param {Date} date - The date to check
 * @param {Array} busyTimesList - List of busy times
 * @returns {boolean} - True if the date has busy times
 */
export const hasBusyTimes = (date, busyTimesList, timezone = 'Europe/London') =>
  getBusyTimesOverlappingDate(date, busyTimesList, timezone).length > 0;

/** Bookings with no space_id still show on every space (legacy rows). */
export const bookingMatchesSpace = (booking, spaceId) => {
  if (!spaceId) return true;
  const sid = booking?.space_id ?? booking?.spaceId;
  if (sid == null || sid === '') return true;
  return String(sid) === String(spaceId);
};

export const filterBookingsBySpace = (bookings, spaceId) => {
  if (!spaceId || !Array.isArray(bookings)) return bookings || [];
  return bookings.filter((b) => bookingMatchesSpace(b, spaceId));
};

/** Mirrors database-api space busy filtering when scoping client-side. */
export const filterBusyTimesBySpace = (busyTimes, spaceId, multiSpaceCalendarsEnabled = false) => {
  if (!spaceId || !Array.isArray(busyTimes)) return busyTimes || [];
  return busyTimes.filter((bt) => {
    const sid = bt?.space_id ?? bt?.spaceId;
    const provider = String(bt?.provider || '').toLowerCase();
    if (multiSpaceCalendarsEnabled) {
      if (sid == null || sid === '') {
        return provider !== 'google';
      }
      return String(sid) === String(spaceId);
    }
    if (sid == null || sid === '') return true;
    return String(sid) === String(spaceId);
  });
};

const CANCELLED_BOOKING_STATUSES = new Set([
  'cancelled',
  'canceled',
  'canceled_by_user',
  'canceled_by_owner',
  'cancelled_by_user',
  'cancelled_by_owner'
]);

const CALENDAR_VISIBLE_STATUSES = new Set([
  'confirmed',
  'pending',
  'accepted',
  'on_hold',
  'upcoming'
]);

/** Normalise booking.status / booking_status for dashboard filters. */
export const normalizeBookingStatusKey = (booking) =>
  String(booking?.status ?? booking?.booking_status ?? 'pending').toLowerCase().trim();

/** Active bookings shown on the home calendar timeline (daily / weekly / monthly). */
export const isCalendarVisibleBooking = (booking) => {
  const primary = String(booking?.status ?? '').toLowerCase().trim();
  const secondary = String(booking?.booking_status ?? '').toLowerCase().trim();
  if (CANCELLED_BOOKING_STATUSES.has(primary) || CANCELLED_BOOKING_STATUSES.has(secondary)) {
    return false;
  }
  return CALENDAR_VISIBLE_STATUSES.has(primary) || CALENDAR_VISIBLE_STATUSES.has(secondary);
};

const isAcceptedBookingSummaryStatus = (status) =>
  isApprovedBookingStatus(status) || status === 'upcoming' || status === 'completed';

/** BOOKING SUMMARY counts — status rows (accepted/pending/canceled) are mutually exclusive. */
export const getBookingSummaryCounts = (bookings) => {
  const counts = {
    total: 0,
    accepted: 0,
    pending: 0,
    paid: 0,
    canceled: 0
  };
  if (!Array.isArray(bookings)) return counts;
  counts.total = bookings.length;
  bookings.forEach((booking) => {
    const status = normalizeBookingStatusKey(booking);
    if (CANCELLED_BOOKING_STATUSES.has(status)) {
      counts.canceled += 1;
    } else if (isAcceptedBookingSummaryStatus(status)) {
      counts.accepted += 1;
    } else {
      counts.pending += 1;
    }
    if (isPaidBookingPaymentStatus(booking?.payment_status ?? booking?.paymentStatus)) {
      counts.paid += 1;
    }
  });
  return counts;
};
